import { z } from 'zod';
import { cliTransportSchema, oltVendorSchema, snmpVersionSchema } from './olt.js';
import { cidrSchema } from './site.js';

/**
 * Pre-onboarding preparation.
 *
 * An OLT has to be prepared before a management platform can do anything with
 * it: a reachable management address and a login. The wizard generates that
 * preparation as commands carrying the operator's own addresses, verifies the
 * login over the wire, and then SETS THE REST UP ITSELF over that login — SNMP,
 * time servers, VLANs — skipping whatever the chassis already has (operator,
 * 2026-09-25: "the wizard instruction should end at enable telnet, the next
 * part should put ntp / snmp / vlans via cli and complete setup, it should
 * also skip what is already there rather than fail").
 */

/** Vendor plus chassis family, because the command set differs within a vendor. */
export const oltPlatformSchema = z.enum([
  'zte-c3xx', // C300 / C320
  'zte-c6xx', // C600 / C610 / C620 / C650
  'huawei-ma5x00', // MA5600T / MA5608T / MA5800
]);
export type OltPlatform = z.infer<typeof oltPlatformSchema>;

export const OLT_PLATFORM_LABELS: Record<OltPlatform, string> = {
  'zte-c3xx': 'ZTE C300 / C320',
  'zte-c6xx': 'ZTE C600 / C610 / C620 / C650',
  'huawei-ma5x00': 'Huawei MA5600T / MA5608T / MA5800',
};

export function vendorOf(platform: OltPlatform): z.infer<typeof oltVendorSchema> {
  return platform.startsWith('huawei') ? 'huawei' : 'zte';
}

export const managementModeSchema = z.enum(['out_of_band', 'in_band']);
export type ManagementMode = z.infer<typeof managementModeSchema>;

// --- generated credentials ------------------------------------------------------

/**
 * The characters a generated password or community is made of.
 *
 * Letters and digits only, because every family's CLI takes those literally —
 * no `?` (a ZTE answers help the instant it arrives), no quote, space, `#`,
 * `!` or `|` — and without 0/O/1/l/I, so a password read off the screen and
 * typed at a console survives the trip.
 */
export const OLT_SECRET_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const LOWER = 'abcdefghijkmnpqrstuvwxyz';
const DIGIT = '23456789';

/**
 * 14 characters: inside every length window the families enforce on a local
 * user (the MA5600T asks for 6–15, the MA5800 and the ZTE lines accept more).
 */
export const OLT_PASSWORD_LENGTH = 14;
/** The random part of a community; with its 6-letter prefix, 20 characters — under every family's 32. */
export const OLT_COMMUNITY_RANDOM_LENGTH = 14;
/** Random bytes a caller supplies for either. */
export const OLT_SECRET_BYTES = 16;

function fromBytes(bytes: ArrayLike<number>, length: number): string[] {
  if (bytes.length < length) throw new Error(`need at least ${length} random bytes`);
  return Array.from({ length }, (_, i) => OLT_SECRET_ALPHABET[bytes[i]! % OLT_SECRET_ALPHABET.length]!);
}

/**
 * A generated CLI password, from random bytes the caller supplies (the
 * browser's crypto, the API's) — pure, so the rules are pinned without
 * randomness. It opens with an upper-case letter, a lower-case letter and a
 * digit: a chassis that checks complexity asks for letters of both cases and a
 * digit, and one that wants a leading letter gets one.
 */
export function oltPasswordFromBytes(bytes: ArrayLike<number>): string {
  const chars = fromBytes(bytes, OLT_PASSWORD_LENGTH);
  chars[0] = UPPER[bytes[0]! % UPPER.length]!;
  chars[1] = LOWER[bytes[1]! % LOWER.length]!;
  chars[2] = DIGIT[bytes[2]! % DIGIT.length]!;
  return chars.join('');
}

/**
 * A generated SNMP community: `sacsro…` to read, `sacsrw…` to write, so the
 * two are told apart on the chassis's own `show running-config`. The random
 * part carries a digit and an upper-case letter because newer Huawei firmware
 * refuses a community of one character class.
 */
export function oltCommunityFromBytes(kind: 'ro' | 'rw', bytes: ArrayLike<number>): string {
  const chars = fromBytes(bytes, OLT_COMMUNITY_RANDOM_LENGTH);
  chars[0] = DIGIT[bytes[0]! % DIGIT.length]!;
  chars[1] = UPPER[bytes[1]! % UPPER.length]!;
  return `sacs${kind}${chars.join('')}`;
}

// --- VLANs the setup puts on the chassis ----------------------------------------

/**
 * A VLAN description the chassis takes on every family — the same rule the
 * OLT's VLANs tab writes with (`assertSafeVlanText` in the API).
 */
export const OLT_VLAN_LABEL_PATTERN = /^[A-Za-z0-9_.\- ]{1,64}$/;

/**
 * One VLAN the wizard puts on the chassis.
 *
 * `management` is the ROLE: the ONUs' own management / TR-069 traffic. Only
 * that role takes a subnet — the one those ONUs get their addresses from,
 * which the gateway must route for the ACS to reach them. A VLAN without the
 * role carries subscribers, and its addressing is the router's business
 * (operator, 2026-09-25: "a vlan without a management role does not need a
 * subnet"), so a subnet sent on one is dropped rather than stored.
 */
export const onboardingVlanSchema = z
  .object({
    vlanId: z.coerce.number().int().min(1).max(4094),
    /** What the operator calls it — written to the chassis as its description, and the org-wide label. */
    name: z
      .string()
      .trim()
      .max(64)
      .optional()
      .transform((v) => (v ? v : undefined))
      .refine((v) => v === undefined || OLT_VLAN_LABEL_PATTERN.test(v), {
        message: 'A VLAN name takes letters, digits, spaces, dot, dash and underscore only.',
      }),
    management: z.boolean().default(false),
    subnet: cidrSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.management && !v.subnet) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['subnet'],
        message: `VLAN ${v.vlanId} is a management VLAN, so it needs the subnet its ONUs take their addresses from.`,
      });
    }
  })
  .transform((v) => ({ ...v, subnet: v.management ? v.subnet : undefined }));
export type OnboardingVlan = z.infer<typeof onboardingVlanSchema>;
/** A row as a client sends it (subnet optional; role defaults off). */
export type OnboardingVlanInput = z.input<typeof onboardingVlanSchema>;

export const onboardingVlansSchema = z
  .array(onboardingVlanSchema)
  .max(32)
  .superRefine((rows, ctx) => {
    const seen = new Set<number>();
    rows.forEach((row, index) => {
      if (seen.has(row.vlanId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, 'vlanId'],
          message: `VLAN ${row.vlanId} is listed twice.`,
        });
      }
      seen.add(row.vlanId);
    });
  });

export const prepInputSchema = z.object({
  platform: oltPlatformSchema,
  name: z.string().min(2).max(120),
  /** Address the OLT will answer on once prepared. */
  mgmtIp: z.string().min(7).max(45),
  mgmtNetmask: z.string().default('255.255.255.0'),
  mgmtGateway: z.string().min(7).max(45),
  managementMode: managementModeSchema.default('out_of_band'),
  /** In-band only: the VLAN carrying the OLT's own management. */
  mgmtVlan: z.coerce.number().int().min(1).max(4094).optional(),
  /** In-band only: the uplink port the VLAN is tagged on. */
  uplinkPort: z.string().max(60).optional(),
  cliUsername: z.string().min(1).max(60).default('simpleacs'),
  /** Generated by the wizard; the operator types it into the chassis in the user block. */
  cliPassword: z.string().max(200).optional(),
  cliTransport: cliTransportSchema.default('telnet'),
  snmpVersion: snmpVersionSchema.default('2c'),
  /**
   * The read and the write community, both generated by the wizard and put on
   * the chassis BY THE PLATFORM during setup — the operator never types them.
   * Read-write because the platform writes over SNMP now (ONU description,
   * admin state, reboot, the write health check).
   */
  snmpCommunity: z.string().min(1).max(200),
  snmpCommunityRw: z.string().min(1).max(200).optional(),
  /** The gateway whose tunnel reaches this OLT, when it is not directly routable. */
  siteId: z.string().nullable().optional(),
  /** VLANs to put on the chassis during setup, each with its role. */
  vlans: onboardingVlansSchema.default([]),
  /**
   * Time servers to point the chassis at.
   *
   * Asked here because onboarding is the one moment somebody is looking at a
   * chassis with intent to configure it — and a wrong clock is not noticed
   * until it is read back in a log six months later, by which time every
   * event on the OLT has the wrong time on it.
   */
  ntpServers: z.array(z.string().min(1).max(255)).max(8).optional(),
  ntpInterval: z.coerce.number().int().min(4).max(1024).optional(),
  /**
   * The IANA zone this chassis sits in.
   *
   * RECORDED at onboarding and not written — the timezone input form has not
   * been confirmed on hardware for either vendor, and a wrong one moves every
   * timestamp the chassis reports by hours. Writing it is a separate,
   * confirmed act from the OLT's Time card.
   */
  timezone: z.string().max(64).nullable().optional(),
});
export type PrepInput = z.infer<typeof prepInputSchema>;

export const commandBlockSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** Why this block exists, in one sentence. */
  rationale: z.string(),
  /** Where the commands run: the OLT console, or the site router. */
  target: z.enum(['olt', 'router']),
  commands: z.array(z.string()),
  /** Set when the syntax is not from the vendor's own guide. */
  verifyOnHardware: z.boolean(),
  notes: z.array(z.string()),
});
export type CommandBlock = z.infer<typeof commandBlockSchema>;

export const prepPlanSchema = z.object({
  platform: oltPlatformSchema,
  vendor: oltVendorSchema,
  /** How to reach the console before there is any IP configuration. */
  console: z.object({
    method: z.string(),
    defaultAddress: z.string().nullable(),
    defaultCredentials: z.string().nullable(),
    notes: z.array(z.string()),
  }),
  /** What the operator types on the OLT — ending at enabling the CLI transport. */
  blocks: z.array(commandBlockSchema),
  /**
   * The site router's half of each management VLAN: its gateway address and
   * DHCP. Not the OLT's and not the platform's to apply, so shown apart — on
   * the setup step, beside what the platform did.
   */
  routerBlocks: z.array(commandBlockSchema),
  /** What the wizard will check once the operator says the OLT is prepared. */
  willVerify: z.array(z.string()),
  /** What the platform then sets up by itself, over the login. */
  willSetUp: z.array(z.string()),
});
export type PrepPlan = z.infer<typeof prepPlanSchema>;

export const checkStatusSchema = z.enum(['pass', 'warn', 'fail', 'skipped']);
export type CheckStatus = z.infer<typeof checkStatusSchema>;

export const preflightCheckSchema = z.object({
  id: z.string(),
  label: z.string(),
  status: checkStatusSchema,
  detail: z.string(),
  /** What to do about it, when it is not a pass. */
  remedy: z.string().nullable(),
});
export type PreflightCheck = z.infer<typeof preflightCheckSchema>;

export const preflightResultSchema = z.object({
  ready: z.boolean(),
  checks: z.array(preflightCheckSchema),
  detectedVendor: oltVendorSchema.nullable(),
  detectedModel: z.string().nullable(),
  sysDescr: z.string().nullable(),
});
export type PreflightResult = z.infer<typeof preflightResultSchema>;

/** Everything the wizard collected, submitted as one call at the end. */
export const completeOnboardingSchema = prepInputSchema.extend({
  snmpPort: z.coerce.number().int().min(1).max(65535).default(161),
  cliPort: z.coerce.number().int().min(1).max(65535).default(23),
  /** Superadmin may place the OLT in another organization. */
  orgId: z.string().optional(),
  /**
   * Start the setup (SNMP, time, sweep, VLANs, gateway) straight after
   * registering — what the wizard always does. False registers the OLT and
   * touches no chassis: for a script, or a chassis whose setup should wait for
   * `POST /olts/:id/setup`.
   */
  setup: z.boolean().default(true),
  /**
   * Whether the new OLT may show an unseen-event count and a warning triangle
   * on the OLT list.
   *
   * `default(true)` on purpose, and it is the same answer omitting the field
   * gives: a chassis nobody has an opinion about yet behaves like every OLT
   * already on the fleet. It is offered here because a chassis whose chatter
   * an operator already knows about — a hand-over from another platform, a
   * site with flaky mains — should not have to be silenced after the fact.
   *
   * Display only. It never affects whether events are recorded, and it never
   * touches the findings the notification bell reads.
   */
  eventNotifications: z.boolean().default(true),
});
export type CompleteOnboardingRequest = z.infer<typeof completeOnboardingSchema>;

// --- the setup the platform does itself -------------------------------------------

/**
 * The steps, in the order they run. SNMP first so the sweep can read; time
 * next; the sweep before the VLANs so VLANs the chassis already carries are
 * known and skipped; the gateway last, because it is the one step that
 * changes something that is not the chassis.
 */
export const OLT_SETUP_STEPS = ['snmp', 'ntp', 'sweep', 'vlans', 'gateway'] as const;
export const oltSetupStepIdSchema = z.enum(OLT_SETUP_STEPS);
export type OltSetupStepId = z.infer<typeof oltSetupStepIdSchema>;

/**
 *   done     — the platform changed something, and it took
 *   already  — nothing to change: the chassis (or gateway) had it
 *   skipped  — not applicable, or not the caller's to change — `detail` says which
 *   failed   — tried and refused; the chassis's own words, credentials masked
 *   planned  — a dry run's "would do"
 */
export const oltSetupStepStatusSchema = z.enum(['done', 'already', 'skipped', 'failed', 'planned']);
export type OltSetupStepStatus = z.infer<typeof oltSetupStepStatusSchema>;

export const oltSetupStepSchema = z.object({
  id: oltSetupStepIdSchema,
  label: z.string(),
  status: oltSetupStepStatusSchema,
  /** What happened, in plain words. Never a command line. */
  detail: z.string(),
  /**
   * The write this step makes has not been run by the platform on this
   * chassis family yet — built from captured grammar, awaiting a chassis to
   * prove it on (operator, 2026-09-25: build for both families, "just leave
   * it for testing where we dont have an olt to test").
   */
  unverified: z.boolean(),
});
export type OltSetupStep = z.infer<typeof oltSetupStepSchema>;

export const oltSetupResultSchema = z.object({
  oltId: z.string(),
  dryRun: z.boolean(),
  steps: z.array(oltSetupStepSchema),
});
export type OltSetupResult = z.infer<typeof oltSetupResultSchema>;

/** `POST /olts/:id/setup` — also what onboarding starts on its own. */
export const startOltSetupSchema = z.object({
  vlans: onboardingVlansSchema.default([]),
  /**
   * Which generation, for a chassis whose model no sweep has read yet — the
   * SNMP step runs first, and a C650 and a C300 take different lines.
   */
  platform: oltPlatformSchema.optional(),
  /** Read the chassis and say what would be done; change nothing anywhere. */
  dryRun: z.boolean().default(false),
});
export type StartOltSetupRequest = z.infer<typeof startOltSetupSchema>;

// --- credentials typed for a chassis the platform did not prepare -------------------

/**
 * A login and communities for a chassis that already exists — a SmartOLT
 * migration's OLTs, whose API never returns them (operator, 2026-09-25: "a
 * smartolt migration should include OLT and its credentials so it can pull
 * data not available via smartolt api").
 */
export const oltCredentialInputSchema = z.object({
  cliTransport: cliTransportSchema.default('telnet'),
  cliPort: z.coerce.number().int().min(1).max(65535).optional(),
  cliUsername: z.string().trim().min(1).max(60),
  cliPassword: z.string().min(1).max(200),
  snmpVersion: snmpVersionSchema.default('2c'),
  snmpPort: z.coerce.number().int().min(1).max(65535).optional(),
  snmpCommunity: z.string().trim().min(1).max(200),
  snmpCommunityRw: z
    .string()
    .trim()
    .max(200)
    .optional()
    .transform((v) => (v ? v : undefined)),
});
export type OltCredentialInput = z.infer<typeof oltCredentialInputSchema>;

/** `POST /olts/credential-check` — read-only: a login, and an SNMP read with each community. */
export const oltCredentialCheckSchema = oltCredentialInputSchema.extend({
  vendor: oltVendorSchema,
  mgmtIp: z.string().min(7).max(45),
  /**
   * The organization the chassis belongs to — its gateways decide which
   * address is actually dialled. A tenant's own org is used regardless; a
   * superadmin names it.
   */
  orgId: z.string().optional(),
});
export type OltCredentialCheckRequest = z.infer<typeof oltCredentialCheckSchema>;

export const oltCredentialCheckResultSchema = z.object({
  cli: z.object({ ok: z.boolean(), detail: z.string() }),
  snmpRead: z.object({ ok: z.boolean(), detail: z.string() }),
  /** Null when no write community was given. */
  snmpWrite: z.object({ ok: z.boolean(), detail: z.string() }).nullable(),
});
export type OltCredentialCheckResult = z.infer<typeof oltCredentialCheckResultSchema>;
